import {ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import {
  BaseDialogComponent,
  RefModelStoreService,
  SnackBarService
} from '@q9elements/ui-kit/common';
import {Q9Validators} from '@q9elements/ui-kit/validators';
import {BehaviorSubject, Observable} from 'rxjs';

import {CustomViewDaoService} from '../../../../../../core/services/custom-view-dao.service';
import {ICustomView, ICustomViewColumn} from '../../../../../models/custom-view.interface';
import {CustomViewService} from '../../../../../services/custom-view.service';
import {SortDialogData} from '../../models/create-new-view.interface';

@Component({
  selector: 'app-edit-filters-dialog',
  styleUrls: ['./edit-sort-dialog.component.scss'],
  templateUrl: './edit-sort-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditSortDialogComponent extends BaseDialogComponent implements OnInit, OnDestroy {
  public title = 'RUBIC_CUBE.EDIT_SORT_DIALOG_TITLE';
  public customView: ICustomView;
  public remove: boolean = false;
  public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public formGroup: FormGroup;

  public directions = {
    string: [
      {
        value: 'asc',
        label: 'RUBIC_CUBE.STRING_SORT_ASCENDING'
      },
      {
        value: 'desc',
        label: 'RUBIC_CUBE.STRING_SORT_DESCENDING'
      }
    ],
    date: [
      {
        value: 'asc',
        label: 'RUBIC_CUBE.DATE_SORT_ASCENDING'
      },
      {
        value: 'desc',
        label: 'RUBIC_CUBE.DATE_SORT_DESCENDING'
      }
    ],
    integer: [
      {
        value: 'asc',
        label: 'RUBIC_CUBE.INTEGER_SORT_ASCENDING'
      },
      {
        value: 'desc',
        label: 'RUBIC_CUBE.INTEGER_SORT_DESCENDING'
      }
    ],
    boolean: [
      {
        value: 'asc',
        label: 'RUBIC_CUBE.BOOLEAN_SORT_ASCENDING'
      },
      {
        value: 'desc',
        label: 'RUBIC_CUBE.BOOLEAN_SORT_DESCENDING'
      }
    ]
  };
  public chosenType: string;
  public columns: ICustomViewColumn[];
  public sortExists: boolean;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SortDialogData,
    public dialogRef: MatDialogRef<EditSortDialogComponent>,
    private customViewService: CustomViewService,
    private translate: TranslateService,
    private customViewDaoService: CustomViewDaoService,
    private rmeStore: RefModelStoreService,
    private snackBarService: SnackBarService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initForm();
    if (!this.data.view) {
      this.fetchCustomView();
    } else {
      this.handleCustomView(this.data.view);
    }
  }

  private initForm() {
    this.formGroup = new FormGroup({
      column: new FormControl('', Q9Validators.required()),
      subcolumn: new FormControl(''),
      subcolumnParent: new FormControl(''),
      direction: new FormControl('', Q9Validators.required())
    });
  }

  private handleCustomView(view: ICustomView) {
    this.customView = view;
    this._initColumns();
    this._applySort();
    this.loading$.next(false);
  }

  private fetchCustomView() {
    const refModel = this.rmeStore.refModel;
    this.customViewDaoService.getCustomView(refModel._id, this.data.viewId).subscribe(
      response => {
        this.customView = response;
        this._initColumns();
        this._applySort();
        this.loading$.next(false);
      },
      error => {
        this.snackBarService.error(error.error.error);
        this.closeModal();
      }
    );
  }

  private _initColumns() {
    const {columns: columnsFromView} = this.customView;
    this.columns = columnsFromView.filter(column => !column.filterOnly && !column.disableSort);
    this.columns = this.columns.map(column => {
      const modifiedColumn = {...column};

      if (modifiedColumn.subcolumn) {
        modifiedColumn.subcolumnParent = modifiedColumn.value;
        modifiedColumn.displayValue = modifiedColumn.subcolumn.displayValue;
        modifiedColumn.value = modifiedColumn.subcolumn.value;
      }

      return modifiedColumn;
    });
  }

  public changeRemove(value: boolean) {
    this.remove = value;
  }

  private _applySort() {
    if (!this.customView.sort) {
      return;
    }

    const sortColumn = this.columns.find(column => {
      return (
        column.value === this.customView.sort.column ||
        column.value === this.customView.sort.subcolumn
      );
    });
    if (!sortColumn) {
      return;
    }

    this.chosenType = sortColumn.dbValue;
    const sortOrder = this.directions[this.chosenType].find(
      item => item.value === this.customView.sort.direction
    );

    this.sortExists = true;

    this.formGroup.setValue({
      column: sortColumn.value,
      subcolumn: this.customView.sort.subcolumn || '',
      subcolumnParent: sortColumn.subcolumnParent || '',
      direction: sortOrder.value
    });
  }

  public closeModal() {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.customViewService.resetModal();
  }

  public updateSubColumn($event) {
    const column = this.columns.find(item => item.value === $event.value);
    this.chosenType = column?.dbValue || this.chosenType;
    if (column?.subcolumnParent) {
      this.formGroup.patchValue({
        subcolumn: $event.value,
        subcolumnParent: column.subcolumnParent
      });
    } else {
      this.formGroup.patchValue({
        subcolumn: '',
        subcolumnParent: ''
      });
    }
  }

  override onSubmit() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.formGroup.markAsDirty();
      return;
    }
    this.sendSubmitRequest();
  }

  protected override sendSubmitRequest(): Observable<unknown> {
    const {refModel, _id, dynamicalView} = this.customView;

    const payload = {
      sort: this.remove
        ? null
        : {
            column: this.formGroup.value.subcolumnParent
              ? `${this.formGroup.value.subcolumnParent}.${this.formGroup.value.subcolumn}`
              : this.formGroup.value.column,
            direction: this.formGroup.value.direction
          }
    };

    if (dynamicalView) {
      this.dialogRef.close(payload);

      return;
    }

    this.customViewDaoService.updateSort(refModel, _id, payload).subscribe(
      response => {
        this.customViewService.updateCustomView(response);

        const msg = this.translate.instant('RUBIC_CUBE.EDITED_SORT_SUCCESSFULLY', {
          name: this.customView.name
        });
        this.dialogRef.close({data: response, msg});
      },
      error => {
        const msg = this.translate.instant(error.error || 'RUBIC_CUBE.EDITED_FILTERS_FAILURE', {
          name: this.customView.name
        });
        this.dialogRef.close({msg});
      }
    );

    return;
  }
}
